import assert from "assert";

import SQL from 'mssql'
import { Enum } from "./data-helpers";
import { enumByIdentifier } from "./data-helpers";
import { DateOnly, dateOnly, Decimal, isFractionDigits, Money } from "./data-helpers/likables";
import { isNumber } from "./data-helpers/misc";
import { addHours } from "date-fns";

export * from "./data-helpers/enums";
export * from "./data-helpers/misc";
export * from "./data-helpers/likables";
export * from "./data-helpers/temp-workarounds";

export const deepCopy = <T, U = T extends Array<infer V> ? V : never>(source: T ): T => {
  if (Array.isArray(source)) {
    return source.map(item => (deepCopy(item))) as T & U[]
  }
  if (source instanceof Date) {
    return new Date(source.getTime()) as T & Date
  }
  if (source && typeof source === 'object') {
    if ("__skipDeepCopy" in source && source.__skipDeepCopy) return source;
    return (Object.getOwnPropertyNames(source) as (keyof T)[]).reduce<T>((o, prop) => {
      Object.defineProperty(o, prop, Object.getOwnPropertyDescriptor(source, prop)!)
      o[prop] = deepCopy(source[prop])
      return o
    }, Object.create(Object.getPrototypeOf(source)))
  }
  return source
}

type Row = Record<string, any>;
export function rows<TRow extends Row>(result: SQL.IResult<TRow>) {
  return result.recordset
    .map(cleanRow)
}
function cleanRow<TRow extends Row>(_: TRow) {
  const cleanEntries = Object.entries(_)
    .map(([k, v]) => [k, v === null ? undefined : v]);
  return Object.fromEntries(cleanEntries) as TRow
}

export function defined<T>(_: T | undefined): T {
  assert(_ !== undefined);
  return _!;
}

export function areEqualArrays(arr1: string[], arr2: string[]): boolean {
  return arr1.length === arr2.length && arr1.every((element, index) => element === arr2[index]);
}

export function areUnique(arr: string[]): boolean {
  return new Set(arr).size === arr.length;
}

export function toRawEnums<TEnum extends Enum>(enums: TEnum[]) {
  assert(isEnums(enums));
  return (_: TEnum[]): string | undefined => {
    assert(_.every(_ => enums.includes(_)));
    const candidate = _.map(_ => _.identifier).join(",");
    return candidate === "" ? undefined : candidate;
  }
}
export function fromRawEnums<TEnum extends Enum>(enums: TEnum[]) {
  assert(isEnums(enums));
  return (_: string | undefined): TEnum[] => {
    if (_ === undefined) return [];
    return _.split(",").map(_ => enumByIdentifier(enums)(_));
  }
}

const isEnums = <TEnum extends Enum>(enums: TEnum[]) => {
  if (enums === undefined) return false;
  if (enums === null     ) return false;
  if (enums.length === 0 ) return false;

  return enums.every(_ => _ !== undefined);
}

export function dateOnly_FROM_date(date: Date): DateOnly {
  const result = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0));
  return dateOnly(result);
}
export function optionalDateOnly_FROM_optionalDate(date?: Date): DateOnly | undefined {
  if (date === undefined) return undefined;
  return dateOnly_FROM_date(date);
}
export function year_FROM_date(date: Date): number {
  return date?.getUTCFullYear();
}
export function date_WITH(date: Date, { year }: { year: number }): Date {
  const newDate = new Date(date.getTime());
  newDate.setUTCFullYear(year);
  return newDate;
}

// CLIFF: Adding hours is required to account for Daylight Saving Time changes when adding days.
export const dateOnly_ADD = (date: DateOnly, { days }: { days: number }) => dateOnly(addHours(date, days * 24));

export const decimal_FROM_number = (fractionDigits: number) => {
  assert(isFractionDigits(fractionDigits));
  return (_: number): Decimal => {
    assert(isNumber(_));
    return Number((_).toFixed(fractionDigits)) as Decimal;
  }
}

export function money_FROM_number(number: number): Money {
  return decimal_FROM_number(2)(number) as unknown as Money;
}

export type Predicate<T> = (value: T) => boolean;
export type Mapper<TInput, TOutput> = (input: TInput) => TOutput;
export type Comparator<T> = (a: T, b: T) => number;

const opposite = <T>(predicate: Predicate<T>) => (value: T) => !predicate(value);

const evaluate = <T>(predicate: Predicate<T> | boolean | undefined, value: T) => {
  if (predicate === undefined) return undefined;
  if (typeof predicate === 'boolean') return predicate;
  return predicate(value);
}

export const ALWAYS = () => true;
export const NEVER  = () => false;

export const WHEN = <T>(condition?: boolean | Predicate<T>, default_?: Predicate<T>) => {
  if (       condition === undefined) return default_ ?? NEVER;
  if (typeof condition === 'boolean') return () => condition;
  return condition;
}
export const UNLESS = <T>(condition: undefined | boolean | Predicate<T>, default_?: Predicate<T>) => {
  if (       condition === undefined) return opposite(default_ ?? NEVER);
  if (typeof condition === 'boolean') return opposite(() => condition);
  return opposite(condition);
}
export const SOME  = <T>(...predicates: (Predicate<T> | boolean | undefined)[]) => (value: T) => predicates.some (predicate =>   evaluate(predicate, value) ?? false);
export const EVERY = <T>(...predicates: (Predicate<T> | boolean | undefined)[]) => (value: T) => predicates.every(predicate =>   evaluate(predicate, value) ?? false);
export const NONE  = <T>(...predicates: (Predicate<T> | boolean | undefined)[]) => (value: T) => predicates.every(predicate => !(evaluate(predicate, value) ?? false));

// Date helpers

export const WHEN_WITHIN = (days: number) => ({ dueDate, now }: { dueDate: Date, now: Date }) =>
  (dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24) <= days;

// URL helpers

export function joinUrl(...parts: string[]): string {
  const parts_ = parts.map((part, index) => {
    const isFirst = index === 0;
    const isLast  = index === parts.length - 1;

    if (isFirst && isLast) return part;

    return isFirst ? part.replace(/(\/$)/g, '')
         : isLast  ? part.replace(/(^\/)/g, '')
                   : part.replace(/(^\/)|(\/$)/g, '');
  });

  return parts_.join('/');
}

export function parseCsv(fileText: string): { headers: string[], data: Record<string, string>[] } {
  const lines = fileText.split("\n").map(line => line.trim()).filter(line => line.length > 0);
  const headers = lines[0].split(",").map(header => header.trim());
  const data = lines.slice(1).map(line => {
    const values = line.split(",").map(value => value.trim());
    return Object.fromEntries(headers.map((header, index) => [header, values[index]]));
  });
  return { headers, data };
}
